import * as React from 'react';
import { escape } from '../../utils/component/helpers';
import { logCreativeClick, logCreativeLoad } from '../../utils/logger';
import { setUrlHttpsIfMissing } from '../../utils/string';
import { ContentMarketing as ContentMarketingT } from '../../../shared/types/contentMarketing';
import { useEffect, useMemo } from 'react';
import { getConfig, getExperimentVariantFromConfig } from '../../config';
import { UNLEASH_FEATURE_NAMES } from '../../../shared/unleash-feature-names';

type Props = {
    positionId: string;
    imagePlacement?: 'top' | 'right' | 'left';
    isFrontpage?: boolean;
    rowIndex?: number;
    contentMarketing: ContentMarketingT;
};

export function ContentMarketing(props: Props) {
    const {
        positionId,
        isFrontpage = false,
        rowIndex,
        contentMarketing,
        imagePlacement = 'top',
    } = props;

    const isRow = useMemo(() => rowIndex !== undefined, [rowIndex]);

    const { adType, elementType } = useMemo(() => {
        return {
            adType: isRow ? 'Content' : 'Content',
            elementType: isRow
                ? rowIndex === 0
                    ? 'Split Left'
                    : 'Split Right'
                : undefined,
        };
    }, [isRow, rowIndex]);

    const { verticalName, subVertical } = useMemo(() => {
        const config = getConfig();
        return {
            verticalName: config.vertical,
            subVertical: config.subvertical,
        };
    }, []);

    useEffect(() => {
        if (document) {
            if (contentMarketing) {
                logCreativeLoad({
                    creativeId: contentMarketing.id,
                    elementId: positionId,
                    verticalName,
                    subVertical,
                    elementType,
                    adType,
                    adFormat: isRow ? 'Split-view' : 'Single-view',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionId]);

    if (contentMarketing) {
        const htmlContent = {
            escapedImgUrl: escape(contentMarketing.image.url),
            escapedIconUrl: escape(contentMarketing.icon.url),
            escapedSponsor: escape(contentMarketing.sponsor),
            escapedHref: escape(setUrlHttpsIfMissing(contentMarketing.href)),
            escapedTitle: escape(contentMarketing.title),
            escapedText: escape(contentMarketing.text),
        };

        let containerStyle = '',
            articleStyle = 'mx-16 ';

        if (isRow) {
            articleStyle = 'h-full ';
            containerStyle = 'flex-auto ';
        }
        if (isFrontpage) {
            articleStyle = '';
            containerStyle += 'mb-16';
        }

        return (
            <div
                onClick={() =>
                    logCreativeClick({
                        creativeId: contentMarketing.id,
                        elementId: positionId,
                        verticalName,
                        subVertical,
                        targetId: htmlContent.escapedHref,
                        provider: htmlContent.escapedSponsor,
                        adType,
                        elementType,
                        adFormat: isRow ? 'Split-view' : 'Single-view',
                    })
                }
                id={positionId}
                className={containerStyle}
            >
                <article
                    className={`relative overflow-hidden transition-all outline-none sf-ad-outline sf-ad-card rounded-8 mb-auto ${articleStyle}`}
                    tabIndex={0}
                >
                    <button className="sr-only" tabIndex={-1}>
                        Velg
                    </button>
                    <div aria-owns="title_id" />
                    {isRow ? (
                        <RowComponent htmlContent={htmlContent} />
                    ) : imagePlacement === 'right' ? (
                        <ImageRightComponent htmlContent={htmlContent} />
                    ) : (
                        <ControlComponent htmlContent={htmlContent} />
                    )}
                </article>
            </div>
        );
    }
    return null;
}

export const MarketingLabel = ({
    escapedSponsor,
    escapedIconUrl,
    justifyBetween = true,
}) => {
    return (
        <div
            className={`flex w-full ${
                justifyBetween ? 'justify-between px-16' : ''
            } items-center bg-white py-8`}
        >
            <div
                className={
                    justifyBetween
                        ? 'text-12 md:text-14'
                        : 'mr-8 text-12 md:text-14'
                }
            >
                Annonsørinnhold
            </div>
            <img
                alt={escapedSponsor}
                className="h-12 md:h-16"
                src={escapedIconUrl}
            />
        </div>
    );
};

type ContentTextType = {
    isRow?: boolean;
    isAbsolute?: boolean;
    isColumn?: boolean;
    escapedSponsor: string;
    escapedHref: string;
    escapedTitle: string;
    escapedText: string;
};

const ContentText = ({
    isRow,
    isColumn,
    escapedSponsor,
    escapedHref,
    escapedTitle,
    escapedText,
}: ContentTextType) => {
    const stickTitleVariant = getExperimentVariantFromConfig(
        UNLEASH_FEATURE_NAMES.stickTitleBold,
    );
    const hasStickTitle = !!escapedText?.length;
    let stickTitleClassName = 'ml-16 mr-16 mt-8 mb-0 ';
    let titleClassName = 'font-normal text-gray-400 mx-16 ';
    let sponsorClassName = 'mx-16 mb-8';

    if (isColumn) {
        titleClassName += '';
    } else {
        titleClassName += ' my-16 mt-4 ';
        sponsorClassName = 'mx-16 my-16';
    }

    if (escapedSponsor.length) {
        sponsorClassName += ' mb-8';
    }

    if (isRow || isColumn) {
        stickTitleClassName += 'text-12 ';
        titleClassName += 'text-16 ';
        sponsorClassName += ' text-gray-500 text-14 ';
    } else {
        stickTitleClassName += 'text-12 md:text-14 ';
        titleClassName += 'text-22 ';
    }

    if (stickTitleVariant === 'stickTitleBold') {
        stickTitleClassName += 'font-bold';
    }

    return (
        <div>
            <div className={sponsorClassName}>
                <span>{escapedSponsor}</span>
            </div>
            {hasStickTitle && (
                <p
                    className={stickTitleClassName}
                    dangerouslySetInnerHTML={{ __html: escapedText }}
                />
            )}
            <h3 className={titleClassName} id="title_id">
                <a
                    style={{ textDecoration: 'none' }}
                    className="link link--dark sf-ad-link text-gray-700 hover:no-underline focus:no-underline focus-ring"
                    href={escapedHref}
                    target="_blank"
                    title={escapedTitle}
                    rel="noreferrer"
                >
                    <span aria-hidden="true" className="inset-0 absolute" />
                    {escapedTitle}
                </a>
            </h3>
        </div>
    );
};

const ImageRightComponent = ({ htmlContent }) => {
    return (
        <a
            style={{ textDecoration: 'none' }}
            className="link link--dark sf-ad-link text-gray-700 hover:no-underline focus:no-underline focus-ring"
            href={htmlContent.escapedHref}
            target="_blank"
            title={htmlContent.escapedTitle}
            rel="noreferrer"
        >
            <span aria-hidden="true" className="inset-0 absolute" />
            <div className={'flex '}>
                <div className={'w-3/5 p-16 relative'}>
                    <div className={'flex items-center justify-between'}>
                        <img
                            alt={htmlContent.escapedSponsor}
                            className="h-12 md:h-16"
                            src={htmlContent.escapedIconUrl}
                        />
                        {htmlContent.escapedSponsor ? (
                            <span className={'font-12'}>
                                {htmlContent.escapedSponsor}
                            </span>
                        ) : (
                            ''
                        )}
                    </div>

                    <div className={'mt-12 absolute mr-16'}>
                        {!!htmlContent.escapedText?.length && (
                            <p
                                className={'font-14 mb-0'}
                                dangerouslySetInnerHTML={{
                                    __html: htmlContent.escapedText,
                                }}
                            />
                        )}
                        <h3
                            className={`font-normal ${
                                htmlContent.escapedTitle?.length > 100
                                    ? 'h4'
                                    : 'h3'
                            }`}
                            id="title_id"
                            style={{
                                textDecoration: 'none',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {htmlContent.escapedTitle}
                        </h3>
                    </div>
                </div>
                <div className={'w-2/5 relative'}>
                    <div
                        className={
                            'absolute text-12 top-0 left-0 px-8 py-4 rounded-br-lg bg-gray-100'
                        }
                        style={{ borderBottomRightRadius: 8 }}
                    >
                        Annonsørinnhold
                    </div>
                    <img
                        alt="Annonsebilde"
                        className="h-290 w-full object-cover"
                        src={htmlContent.escapedImgUrl}
                    />
                </div>
            </div>
        </a>
    );
};

const ControlComponent = ({ htmlContent }) => {
    return (
        <>
            <MarketingLabel
                justifyBetween={true}
                escapedIconUrl={htmlContent.escapedIconUrl}
                escapedSponsor={htmlContent.escapedSponsor}
            />
            <img
                alt="Annonsebilde"
                className="h-290 w-full object-cover"
                src={htmlContent.escapedImgUrl}
            />
            <ContentText
                escapedSponsor={htmlContent.escapedSponsor}
                escapedTitle={htmlContent.escapedTitle}
                escapedHref={htmlContent.escapedHref}
                escapedText={htmlContent.escapedText}
                isRow={false}
            />
        </>
    );
};

const RowComponent = ({ htmlContent }) => (
    <>
        <img
            alt="Annonsebilde"
            className="h-290 w-full object-cover"
            src={htmlContent.escapedImgUrl}
        />
        <ContentText
            escapedSponsor={htmlContent.escapedSponsor}
            escapedTitle={htmlContent.escapedTitle}
            escapedHref={htmlContent.escapedHref}
            escapedText={htmlContent.escapedText}
            isRow={true}
        />
    </>
);
